/**
 * @fileoverview Week dates, time slots, and scroll position for CustomCalendar.
 * @module components/CustomCalendar/useCustomCalendarWeek
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { logger } from "../../utils/logger.js";
import styles from "./CustomCalendar.module.css";
import {
  generateCalendarHourLabels,
  generateCalendarTimeSlots,
  getAppointmentDurationMinutes,
  getCellsPerHour,
  parseBreakTimes,
} from "../../utils/calendar/calendarTimeUtils.js";
import { buildWeekdayDates } from "./customCalendarUtils.js";

function syncCalendarWeek(eventData, setCurrentDate, setWeekDates) {
  logger.info("CustomCalendar received eventData:", { data: eventData });
  if (eventData && eventData.fechaInicioEvento && eventData.fechaFinEvento) {
    const eventStartDate = new Date(eventData.fechaInicioEvento + "T00:00:00");
    const eventEndDate = new Date(eventData.fechaFinEvento + "T00:00:00");

    logger.info("Event start date:", { data: eventStartDate });
    logger.info("Event end date:", { data: eventEndDate });
    logger.info("Event start date string:", {
      data: eventData.fechaInicioEvento,
    });
    logger.info("Event end date string:", { data: eventData.fechaFinEvento });

    if (isNaN(eventStartDate.getTime()) || isNaN(eventEndDate.getTime())) {
      logger.error("Invalid event dates:", {
        data: [eventData.fechaInicioEvento, eventData.fechaFinEvento],
      });
      return;
    }

    setCurrentDate(eventStartDate);

    const { monday: mondayOfEventWeek, dates: eventWeekDates } =
      buildWeekdayDates(eventStartDate);

    logger.info("Generated event week dates:", { data: eventWeekDates });
    logger.info("Event start date:", { data: eventStartDate });
    logger.info("Event end date:", { data: eventEndDate });
    logger.info("Monday of event week:", { data: mondayOfEventWeek });
    setWeekDates(eventWeekDates);
  } else {
    const today = new Date();
    setCurrentDate(today);
    const { dates: currentWeekDates } = buildWeekdayDates(today);
    setWeekDates(currentWeekDates);
  }
}

function scrollCalendarToCurrentTime(calendarGridRef, timeSlots) {
  if (calendarGridRef.current && timeSlots.length > 0) {
    const now = new Date();
    const currentHour = now.getHours();
    const startHour = timeSlots[0].hour;

    const timeSlotHeight = 60;
    const headerHeight = 62;
    const scrollTop = (currentHour - startHour) * timeSlotHeight + headerHeight;

    calendarGridRef.current.scrollTo({
      top: Math.max(0, scrollTop - 200),
      behavior: "smooth",
    });
  }
}

function updateCurrentTimePosition() {
  const currentTimeElements = document.querySelectorAll("[data-current-minute]");
  currentTimeElements.forEach((element) => {
    const minutePosition = element.getAttribute("data-current-minute");
    if (minutePosition !== null) {
      element.style.setProperty("--current-minute", minutePosition);
    }
  });
}

function updateTimeSlotsCount(timeSlotsLength) {
  const calendarElement = document.querySelector(`.${styles.calendar}`);
  if (calendarElement) {
    calendarElement.style.setProperty("--time-slots-count", timeSlotsLength);
  }
}

export function useCustomCalendarWeek({
  eventData,
  participatingCompanyData,
  language,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const calendarGridRef = useRef(null);

  useEffect(() => {
    syncCalendarWeek(eventData, setCurrentDate, setWeekDates);
  }, [eventData]);

  const appointmentDurationMinutes = getAppointmentDurationMinutes(eventData);
  const breakTimes = parseBreakTimes(participatingCompanyData);

  const hourLabels = useMemo(
    () => generateCalendarHourLabels(eventData, language),
    [eventData, language],
  );

  const timeSlots = useMemo(
    () => generateCalendarTimeSlots(eventData, participatingCompanyData),
    [eventData, participatingCompanyData],
  );

  const cellsPerHour = getCellsPerHour(appointmentDurationMinutes);

  const goToToday = () => {
    setTimeout(() => {
      scrollCalendarToCurrentTime(calendarGridRef, timeSlots);
    }, 100);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollCalendarToCurrentTime(calendarGridRef, timeSlots);
    }, 500);

    return () => clearTimeout(timer);
  }, [weekDates]);

  useEffect(() => {
    updateCurrentTimePosition();
    updateTimeSlotsCount(timeSlots.length);

    const interval = setInterval(updateCurrentTimePosition, 60000);

    return () => clearInterval(interval);
  }, [weekDates, timeSlots.length]);

  return {
    currentDate,
    weekDates,
    calendarGridRef,
    hourLabels,
    timeSlots,
    cellsPerHour,
    breakTimes,
    goToToday,
  };
}
